import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import PartnerOfferingCard from '../../components/PartnerOfferingCard';
import MarketplaceNavbar from '../../components/MarketplaceNavbar';
import "./ProductList.css";
import { FaFilter } from "react-icons/fa";
import { languages } from "../Data/artistProfile";
import { makeAuthenticatedGETRequest, makeUnauthenticatedGETRequest } from '../services/serverHelper';
import { partnerPoints } from '../services/apis';
import { addToPartnerCart } from '../reducer/slices/partnerCartSlice';

const PARTNER_CATEGORIES = [
  "Live Performance", "Workshop", "Merchandise", "Digital Product",
  "Booking Service", "Ticketed Event", "Consultation", "Training", "Other",
];

const PARTNER_ART_SECTORS = [
  "Music", "Dance", "Theatre", "Visual Arts", "Comedy",
  "Literature", "Craft", "Mixed Arts", "Other",
];

const BUDGET_BUCKETS = [
  { label: "100 - 1000", min: 100, max: 1000 },
  { label: "1000 - 3500", min: 1000, max: 3500 },
  { label: "3500 - 5000", min: 3500, max: 5000 },
  { label: "5000 and above", min: 5000, max: undefined },
];

/**
 * Shared list/browse page for partner Products and Services.
 * offeringKind: 'Product' | 'Service'
 * pageTitle: heading shown at top (optional)
 */
const PartnerOfferingList = ({ offeringKind, pageTitle }) => {
  const dispatch = useDispatch();
  const { accessToken } = useSelector(state => state.auth);
  const [searchParams, setSearchParams] = useSearchParams({});

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedArtSector, setSelectedArtSector] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');

  const currentPage = Number(searchParams.get('page')) || 1;
  const itemsPerPage = 10;

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    setSelectedCategory(params.category || '');
    setSelectedArtSector(params.artSector || '');
    setSelectedLanguage(params.language || '');
    setSelectedBudget(params.budget || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  const fetchOfferings = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        offeringKind,
        page: currentPage,
        limit: itemsPerPage,
      });

      const category = searchParams.get('category');
      const artSector = searchParams.get('artSector');
      const language = searchParams.get('language');
      const budget = searchParams.get('budget');

      if (category) params.append('category', category);
      if (artSector) params.append('artSector', artSector);
      if (language) params.append('language', language);
      if (budget) {
        const bucket = BUDGET_BUCKETS.find(b => b.label === budget);
        if (bucket) {
          params.append('minPrice', bucket.min);
          if (bucket.max !== undefined) params.append('maxPrice', bucket.max);
        }
      }

      const response = await makeUnauthenticatedGETRequest(
        `${partnerPoints.FETCH_PUBLIC_PARTNER_OFFERINGS_API}?${params}`
      );

      setItems(response?.data?.products || []);
      setPagination(response?.data?.pagination || { total: 0, totalPages: 1 });
    } catch (err) {
      console.error(`Error fetching partner ${offeringKind}s:`, err);
      setError(`Failed to load ${offeringKind === 'Product' ? 'products' : 'services'}. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerCartItems = async () => {
    try {
      const response = await makeAuthenticatedGETRequest(
        `${partnerPoints.GET_PARTNER_CART_ITEMS}/${localStorage.getItem('artId')}`,
        accessToken
      );
      if (response?.statusCode === 200 && response?.data?.partnerCart) {
        dispatch(addToPartnerCart(response.data.partnerCart));
      }
    } catch (err) {
      console.error('Error fetching partner cart items:', err);
    }
  };

  useEffect(() => {
    fetchOfferings();
    if (accessToken) {
      fetchPartnerCartItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offeringKind, searchParams.toString()]);

  const updateFilter = (changes) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(changes).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    if (!Object.prototype.hasOwnProperty.call(changes, 'page')) {
      params.set('page', '1');
    }
    setSearchParams(params);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    updateFilter({ category: e.target.value });
  };

  const handleArtSectorChange = (e) => {
    setSelectedArtSector(e.target.value);
    updateFilter({ artSector: e.target.value });
  };

  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
    updateFilter({ language: e.target.value });
  };

  const handleBudgetChange = (e) => {
    setSelectedBudget(e.target.value);
    updateFilter({ budget: e.target.value });
  };

  const handleResetFilters = () => {
    setSelectedCategory('');
    setSelectedArtSector('');
    setSelectedLanguage('');
    setSelectedBudget('');
    setSearchParams(new URLSearchParams());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOfferings();
  };

  const updatePage = (increment) => {
    const params = new URLSearchParams(searchParams.toString());
    const nextPage = currentPage + increment;
    if (nextPage < 1 || nextPage > (pagination.totalPages || 1)) return;
    params.set('page', nextPage.toString());
    setSearchParams(params);
  };

  const hasActiveFilters = Array.from(searchParams.entries())
    .some(([key, value]) => key !== 'page' && Boolean(value));

  return (
    <div className='marketplace-page'>
      <MarketplaceNavbar />

      <div className='products-main'>
        <div style={{ height: "fit-content", backgroundColor: "white" }} className='ArtistProfiles_Allfilters'>
          <h5 style={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
            <FaFilter />
            All filters
          </h5>
          <div className='ArtistProfiles_Allfilters_form'>
            <form onSubmit={handleSubmit}>

              <div className='ArtistProfiles_Allfilters_form_inputfield'>
                <label>Category</label>
                <select value={selectedCategory} onChange={handleCategoryChange}>
                  <option value="">-- Select Category --</option>
                  {PARTNER_CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className='ArtistProfiles_Allfilters_form_inputfield'>
                <label>Art Sector</label>
                <select value={selectedArtSector} onChange={handleArtSectorChange}>
                  <option value="">-- Select Art Sector --</option>
                  {PARTNER_ART_SECTORS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className='ArtistProfiles_Allfilters_form_inputfield'>
                <label>Budget</label>
                <select value={selectedBudget} onChange={handleBudgetChange}>
                  <option value="" hidden>Select Budget</option>
                  {BUDGET_BUCKETS.map((b) => (
                    <option key={b.label} value={b.label}>{b.label}</option>
                  ))}
                </select>
              </div>

              <div className='ArtistProfiles_Allfilters_form_inputfield'>
                <label>Language</label>
                <select value={selectedLanguage} onChange={handleLanguageChange}>
                  <option value="" hidden>Select Language</option>
                  {languages.map((item, index) => (
                    <option key={index} value={item}>{item}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  style={{ width: '50%', color: '#a40505' }}
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
                <button type='submit' className='filter_btn'>Apply Filters</button>
              </div>

            </form>
          </div>
        </div>

        {error && (
          <div className="error-banner" style={{ margin: '1rem' }}>
            <span>⚠️</span> {error}
          </div>
        )}

        {loading ? (
          <div className="empty-state">
            <p>Loading {offeringKind === 'Product' ? 'products' : 'services'}...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h2>No {offeringKind === 'Product' ? 'Products' : 'Services'} Found</h2>
            <p>
              {hasActiveFilters
                ? 'Try adjusting your search or filter criteria'
                : 'No listings available at this time'}
            </p>
          </div>
        ) : (
          <>
            <div className="marketplace-products-container">
              {items.map((item) => (
                <div key={item._id} className="">
                  <PartnerOfferingCard product={item} />
                </div>
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', margin: '1.5rem 0' }}>
                <button
                  type="button"
                  className='filter_btn'
                  onClick={() => updatePage(-1)}
                  disabled={currentPage <= 1}
                >
                  ← Previous
                </button>
                <span style={{ alignSelf: 'center' }}>
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  className='filter_btn'
                  onClick={() => updatePage(1)}
                  disabled={currentPage >= pagination.totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerOfferingList;
